import { useEffect } from 'react';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import useEventCallback from './useEventCallback';

const DEFAULT_INTERVAL_SECONDS = 90;
const MIN_INTERVAL_SECONDS = 30;

/**
 * Heartbeat di sessione durante la riproduzione.
 *
 * A intervalli regolari verifica (tramite `AccountController.validateSession`) che la sessione
 * dell'utente sia ancora quella attiva. Se non lo è (login da un altro device, token invalidato),
 * invoca `onSessionInvalid`.
 *
 * ABILITAZIONE — disabilitato di default. Si attiva SOLO impostando il parametro custom
 * nella config dell'app (JWP Dashboard → Apps → Custom parameters, oppure `custom` nel config.json):
 *
 *   playbackHeartbeat: "true"
 *   playbackHeartbeatInterval: "90"   // opzionale, secondi (minimo 30)
 *
 * Essendo un parametro di config servito dalla Delivery API, può essere acceso/spento
 * senza rebuild né deploy dell'applicazione.
 */
export const useSessionHeartbeat = (onSessionInvalid: () => void) => {
  const isLoggedIn = useAccountStore((state) => !!state.user);

  const enabled = useConfigStore(({ config }) => String(config.custom?.playbackHeartbeat).toLowerCase() === 'true');
  const intervalSeconds = useConfigStore(({ config }) => {
    const configured = Number(config.custom?.playbackHeartbeatInterval);

    return Number.isFinite(configured) && configured > 0 ? Math.max(configured, MIN_INTERVAL_SECONDS) : DEFAULT_INTERVAL_SECONDS;
  });

  const handleSessionInvalid = useEventCallback(onSessionInvalid);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return;

    let stopped = false;
    let inFlight = false;

    const check = async () => {
      // evita richieste sovrapposte e risparmia chiamate quando la tab è in background
      if (inFlight || (typeof document !== 'undefined' && document.hidden)) return;

      inFlight = true;

      try {
        const accountController = getModule(AccountController);
        const isValid = await accountController.validateSession();

        if (!stopped && !isValid) {
          handleSessionInvalid();
        }
      } catch (error: unknown) {
        // fail-open: un errore imprevisto nel controllo non deve interrompere la visione
        console.warn('[useSessionHeartbeat] session validation failed', error);
      } finally {
        inFlight = false;
      }
    };

    const intervalId = window.setInterval(check, intervalSeconds * 1000);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, isLoggedIn, intervalSeconds, handleSessionInvalid]);
};

export default useSessionHeartbeat;
